import {html } from "lit";
import { state } from 'lit/decorators.js';
import { CheckoutStep, CheckoutStepTemplate } from "./checkout-step";
import { connect } from "pwa-helpers";
import store from "../../store/store";
import { StoreState } from "../../store/store.interface";
import { calculateFreight, getFreightCalculationResponse, getPoOrderNumber, getSelectedShippingMethod, getSelectedShippingTotal, getShippingMethod, isPurchaseOrderApplied, isShippingAddressValid, shippingMethodSelected, updateSelectedShippingMethod } from "./shop-checkout";
import { RmsFreightEstimateType } from "./shop-checkout.interface";
import isEqual  from "lodash/isEqual";
import { getUserDataForUserAnalytics } from "../../features/user-profile/user-profile.selectors";
import sortBy from "lodash/sortBy";
import { loading } from "../../common/constants/api-constants";
import { addAnalyticsCheckoutFlowMetadata } from "../../common/utils/cart-util";

/**
 * Checkout Step to load shipping method options given shipping address. 
 * Save selected method in redux shop checkout state.
 */
export class ShippingMethodCheckoutStepBase extends connect(store)(CheckoutStep) implements CheckoutStepTemplate{

  /**
   * Shipping method options for given shipping address
   */
  @state()
  protected shippingMethodOptions: RmsFreightEstimateType[] = [];

  /**
   * User selected shipping method
   */
  @state()
  protected selectedShippingMethodDetails: RmsFreightEstimateType | undefined  = undefined;

  @state()
  protected isInvalidShippingAddress = false;

  @state()
  protected loadedShippingOptions = false;

  connectedCallback(): void {
    super.connectedCallback();
    this.addTrackedEventListener(window, 'mhecomm-shipping-method-step-invalidate', this.onInvalidateStepEventHandler.bind(this));
  }

  onInvalidateStepEventHandler(_event: Event){
    store.dispatch(shippingMethodSelected(undefined));
    this.forceIncomplete();
  }

  /**
   * On Freight Calculation response, sort freight prices in ascending order.
   * Select previously selected shipping method.
   * @param state 
   */
  onFreightCalculationResponse(state: StoreState){
    this.shippingMethodOptions = sortBy(
      getFreightCalculationResponse(state)?.freightEstimateTypes,
      freightEstimate => this.getTotalFreightPrice(freightEstimate)
    );

    const selectedShippingMethodDetails = getSelectedShippingMethod(state)?.freightEstimateTypes;
    if (selectedShippingMethodDetails) {
      this.selectedShippingMethodDetails = selectedShippingMethodDetails;
    }
  }

  updated(changedProperties: any): void {
    const oldShippingMethodOptions = changedProperties.get('shippingMethodOptions');
    if(oldShippingMethodOptions && !isEqual(oldShippingMethodOptions, this.shippingMethodOptions)){
      const code = (store.getState() as StoreState).shopCheckoutState.selectedShippingMethod?.freightEstimateTypes.shippingMethodCode;
      store.dispatch(updateSelectedShippingMethod(code || this.shippingMethodOptions[0].shippingMethodCode));
    }

    (this.shadowRoot?.querySelector(`#shipping-method-options input:checked`) as HTMLInputElement)?.focus();
  }

  async forceCompleteStep() {
    if(!isShippingAddressValid(store.getState() as StoreState)){
      this.isInvalidShippingAddress = true;
      this.isLoading = false;
    }
    else {
      this.isInvalidShippingAddress = false;
      await store.dispatch(calculateFreight()).unwrap();
      const state = store.getState() as StoreState;
      const code = state.shopCheckoutState.selectedShippingMethod?.freightEstimateTypes?.shippingMethodCode;
      store.dispatch(updateSelectedShippingMethod(code || this.shippingMethodOptions[0].shippingMethodCode));
      this.isLoading = false;
    }
  }


  /**
   * On load.
   */
  loadStep() {
    this.isInvalidShippingAddress = false;
    const state = store.getState() as StoreState;
    if (getFreightCalculationResponse(state)) {
      this.selectedShippingMethodDetails = getSelectedShippingMethod(state)?.freightEstimateTypes;
    } else{
      store.dispatch(calculateFreight());
    }
  }

  /**
   * @returns template of shipping method options as radio buttons.
   */
   renderCheckoutStep(){
    return html`
      <fieldset>
        <legend class="mb-2 fw-semibold fs-6">Select your shipping method:</legend>
        <div id="shipping-method-options">
          ${this.shippingMethodOptions.map( (freightEstimate: RmsFreightEstimateType) => {
            return html`<div class="form-check mb-2">
              <input class="form-check-input" type="radio" name="shipping-method-option"
                id="shp-mth-${freightEstimate.shippingMethodCode}"
                value="${freightEstimate.shippingMethodCode}" 
                ?checked=${this.shouldSelectShippingMethod(freightEstimate.shippingMethodCode)}>
              <label class="form-check-label" for="shp-mth-${freightEstimate.shippingMethodCode}">
                <b class="pe-2 fw-semibold">${this.getFormattedFreightPrice(freightEstimate)}</b> ${freightEstimate.shipMethodDescription}
              </label>
            </div>`
          })}
        </div>
      </fieldset>
      ${this.renderNextStepButton()}
    `;
  }

  getFormattedFreightPrice(freightEstimate: RmsFreightEstimateType) {
    const freightPrice: number =  this.getTotalFreightPrice(freightEstimate);
    if (freightPrice === 0) {
      return "Free";
    }
    return this.getCurrencyPrefix(freightEstimate) + freightPrice.toFixed(2);
  }

  getCurrencyPrefix(freightEstimate: RmsFreightEstimateType) {
    return freightEstimate.freightPrice.currencyCode === "USD" ? "$" : freightEstimate.freightPrice.currencyCode;
  }

  /**
   * @param freightEstimate 
   * @returns Total Freight price = freight price + handling price.
   */
  getTotalFreightPrice(freightEstimate: RmsFreightEstimateType): number {
    return freightEstimate.freightPrice.value + freightEstimate.handlingPrice.value;
  }

  shouldSelectShippingMethod(shippingMethodCode: string): boolean {
    return this.selectedShippingMethodDetails ? this.previouslySelected(shippingMethodCode) : this.isCheapestShippingMethod(shippingMethodCode);
  }

  previouslySelected(shippingMethodCode: string): boolean {
    return !! this.selectedShippingMethodDetails && this.selectedShippingMethodDetails.shippingMethodCode === shippingMethodCode;
  }

  isCheapestShippingMethod(shippingMethodCode: string): boolean{
    return this.shippingMethodOptions[0].shippingMethodCode === shippingMethodCode;
  }

  renderLoadingTemplate() {
    return html`
      <span class="visually-hidden">Loading shipping method step</span>
      ${loading({ width: '43%' })}
      ${loading({ width: '36%' })}
      ${loading({ width: '46%' })}
      ${loading({ width: '35%' })}
      ${loading({ width: '25%', m: 'mt-4' })}
    `;
  }
  /**
   * On click continue to next checkout step, validate that user has selected a shipping method.
   * After successful validation, save selected method in redux shop checkout state.
   */
  override async stepDoValidation() {
    if (this.getSelectedShippingMethod()) {
      store.dispatch(updateSelectedShippingMethod(this.getSelectedShippingMethod().value));
    } else {
      throw new Error("Shipping Method is not selected.");
    }
  }

  override async editStepAction() {
    return;
  }

  /**
   * On click continue to next checkout step, after successful validation, render selected shipping method.
   * @returns 
   */
  renderCompletedStepResults(){
    const freightPrice = getSelectedShippingTotal(store.getState() as StoreState);
    const shipMethodDescription = getSelectedShippingMethod(store.getState() as StoreState)?.freightEstimateTypes?.shipMethodDescription;
    return this.isInvalidShippingAddress ? 
      html`<p>Please update shipping address to see avialable shipping methods.</p>` : 
      html`<p>$${freightPrice.toFixed(2)}</b> ${shipMethodDescription}</p>`;
  }

  protected getSelectedShippingMethod(){
    return this.shadowRoot!.querySelector('[name="shipping-method-option"]:checked') as HTMLInputElement;
  }

  checkoutStepCompleteDetails() {
    const state = store.getState() as StoreState;
    return addAnalyticsCheckoutFlowMetadata({
      id: this.id,
      user: getUserDataForUserAnalytics(state),
      shippingMethod: getShippingMethod(state)?.freightEstimateTypes.shipMethodDescription,
      comp: 'shipping-method-checkout-step',
      label: this.nextStepButtonName,
      url: window.location.href,
      ...(!!getPoOrderNumber(state) && isPurchaseOrderApplied(state) && 
      { 
        paymentType: "Purchase Order", 
        purchaseOrderNumber: getPoOrderNumber(state) 
      })
    })
   }
}